import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "hooks";
import { RootState } from "configureStore";
import {
  SETTINGS_GROUPS,
  findActiveSettingsEntry,
  settingsEntryPath,
} from "./settings-sections";

type CompanyOption = { value: string; label: string };

/**
 * The frame around every settings page: the grouped navigation on the left,
 * and on the right the title of the open entry, one sentence on why it
 * exists, and the page itself. Which entry is open comes from the URL.
 *
 * Below `xl` (1280 px) the page has no room for a 256 px column next to the
 * app's own sidebar, so the list of links is replaced by a section picker
 * above the content.
 */
export default function SettingsLayout() {
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { companiesCollection } = useAppSelector(
    (state: RootState) => state.companySelectReducer
  );

  const companyName = (companiesCollection as CompanyOption[]).find(
    (company) => company.value === companyId
  )?.label;
  const activeEntry = findActiveSettingsEntry(pathname);
  // On a page below an entry (a series page, a template being edited) the picker must not show the
  // entry as chosen: a select fires no change for the option already chosen, so it could not be opened.
  const isBelowActiveEntry =
    !!activeEntry && pathname !== `/app/${companyId}/${settingsEntryPath(activeEntry)}`;

  return (
    <div className="w-full px-4 py-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <header className="mb-5">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("settings_nav_page_title")}
          </h1>
          {companyName && <p className="text-sm text-gray-500">{companyName}</p>}
        </header>

        <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
          {/* A picker, not a side list, below 1280px on purpose: there the app menu and the online-users rail leave too little width for a column beside the pages. */}
          <label className="block xl:hidden">
            <span className="mb-1 block text-sm font-medium text-gray-700">
              {t("settings_nav_pick_section")}
            </span>
            <select
              value={
                activeEntry && !isBelowActiveEntry ? settingsEntryPath(activeEntry) : ""
              }
              onChange={(event) =>
                navigate(`/app/${companyId}/${event.target.value}`)
              }
              className="block min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-base text-gray-900 md:max-w-md md:text-sm"
            >
              {isBelowActiveEntry && (
                <option value="" disabled>
                  ↳ {t(activeEntry.labelKey)}
                </option>
              )}
              {SETTINGS_GROUPS.map((group) => (
                <optgroup key={group.titleKey} label={t(group.titleKey)}>
                  {group.entries.map((entry) => (
                    <option
                      key={settingsEntryPath(entry)}
                      value={settingsEntryPath(entry)}
                    >
                      {t(entry.labelKey)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          <nav
            aria-label={t("settings_nav_page_title")}
            className="hidden w-full shrink-0 rounded-xl border border-gray-200 bg-white p-3 shadow-sm xl:block xl:w-64"
          >
            {SETTINGS_GROUPS.map((group) => (
              <div key={group.titleKey} className="mb-3 last:mb-0">
                <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {t(group.titleKey)}
                </p>
                <ul className="space-y-0.5">
                  {group.entries.map((entry) => {
                    const isActive = entry === activeEntry;
                    return (
                      <li key={settingsEntryPath(entry)}>
                        <Link
                          to={`/app/${companyId}/${settingsEntryPath(entry)}`}
                          aria-current={isActive ? "page" : undefined}
                          className={`block rounded-md px-3 py-1.5 text-sm ${
                            isActive
                              ? "bg-indigo-50 font-medium text-indigo-700"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {t(entry.labelKey)}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>

          <div className="min-w-0 flex-1">
            {activeEntry && (
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t(activeEntry.labelKey)}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {t(activeEntry.leadKey)}
                </p>
              </div>
            )}
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
